import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';
import { useTenant } from '../../contexts/TenantContext';
import { getApiErrorMessage } from '../api/runtimeErrors';
import { useSurveyUiToast } from '../hooks/useSurveyUiToast';
import { ClientMultiSelect } from '../components/SurveyClientsEditor';
import { isMasterTenant } from '../utils/masterTenant';
 
export default function SurveyCreatePage() {
  const { apiFetch } = useSurveyRuntimeApi();
  const { showError } = useSurveyUiToast();
  const { currentTenant } = useTenant();
  const showClientUi = isMasterTenant(currentTenant);
  const navigate = useNavigate();
  const titleInputRef = useRef(null);
  const [title, setTitle] = useState('');
  const [clients, setClients] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState(null);
  const titleMessage = fieldError;
 
  async function onSubmit(e) {
    e.preventDefault();
    const t = title.trim();
    if (!t) {
      setFieldError('Please enter a survey title.');
      titleInputRef.current?.focus();
      titleInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setFieldError(null);
    setSubmitting(true);
    try {
      const r = await apiFetch('/admin/surveys', {
        method: 'POST',
        body: JSON.stringify({
          title: t,
          clients: clients.map((c) => ({
            client_tenant_id: c.client_tenant_id,
            client_name: c.client_name,
          })),
        }),
      });
      navigate(`/survey/${r.id}/edit`, { replace: true });
    } catch (e) {
      showError(getApiErrorMessage(e, 'Unable to create the survey.'));
      setSubmitting(false);
    }
  }
 
  return (
    <div className="page">
      <header className="page-header">
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Create survey
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enter a title. It is required.
        </Typography>
      </header>
 
      <form className="form" onSubmit={(e) => void onSubmit(e)} style={{ maxWidth: '32rem' }}>
        <label className="field">
          <span>Survey title</span>
          <input
            ref={titleInputRef}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setFieldError(null);
            }}
            maxLength={500}
            disabled={submitting}
            autoFocus
            aria-invalid={Boolean(fieldError)}
            aria-describedby="survey-new-title-hint"
          />
        </label>
        {titleMessage ? (
          <p id="survey-new-title-hint" className="field-error">
            {titleMessage}
          </p>
        ) : (
          <p id="survey-new-title-hint" className="muted small" style={{ margin: '-0.25rem 0 0' }}>
            Required.
          </p>
        )}
        {showClientUi && (
          <Box sx={{ mt: 1.5 }}>
            <ClientMultiSelect
              value={clients}
              onChange={setClients}
              disabled={submitting}
              label="Clients (optional)"
            />
            <p className="muted small" style={{ margin: '0.35rem 0 0' }}>
              Select one or more clients this survey is created for — each of them will see it in
              their own workspace. Leave empty to keep it internal.
            </p>
          </Box>
        )}
        <div className="builder-modal-actions">
          <button type="submit" className="btn primary" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create'}
          </button>
          <Link className="btn secondary" to="/survey/manage">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
 